import React, { useEffect, useState } from 'react';
import { X, Activity, ShieldCheck, AlertCircle, RefreshCcw } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DataHealthSweepModal
 *
 * Two-phase UI:
 *   1. Preview — runs a dry-run against the backend and shows the report.
 *   2. Apply — user confirms, live sweep runs, second report is shown.
 *
 * Props:
 *   open, onClose
 */
export const DataHealthSweepModal = ({ open, onClose }) => {
  const password = sessionStorage.getItem('admin_password');
  const [phase, setPhase] = useState('preview'); // 'preview' | 'applied'
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);

  const runPreview = async () => {
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/maintenance/data-health-preview`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      setReport(await r.json());
    } catch (e) {
      logger.error('Preview failed:', e);
      setError(e.message || 'Preview failed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPhase('preview');
      setReport(null);
      setError('');
      runPreview();
    }
  }, [open]);

  const handleApply = async () => {
    if (!window.confirm('Apply all fixes now? This will update the records shown in the preview.')) return;
    setApplying(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/maintenance/data-health-sweep`, {
        method: 'POST',
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      setReport(await r.json());
      setPhase('applied');
    } catch (e) {
      logger.error('Apply failed:', e);
      setError(e.message || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  const totalFixes = report ? (
    (report.valid_until_backfilled?.length || 0)
    + (report.inverse_orphans_repaired?.length || 0)
    + (report.datetime_normalised?.quotes || 0)
    + (report.datetime_normalised?.invoices || 0)
    + (report.datetime_normalised?.receipts || 0)
  ) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="data-health-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold text-foreground">Data Health Sweep</h3>
            {phase === 'applied' && (
              <span className="ml-2 text-xs uppercase font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Applied
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-foreground/60" />
          </button>
        </div>

        <div className="px-6 py-4 flex-1 overflow-y-auto space-y-4">
          {loading ? (
            <div className="text-center py-12 text-foreground/60 text-sm flex items-center justify-center gap-2">
              <RefreshCcw className="w-4 h-4 animate-spin" /> Scanning records…
            </div>
          ) : error ? (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div><strong>Failed:</strong> {error}</div>
            </div>
          ) : report ? (
            <>
              <div className={`p-3 rounded-lg text-sm border ${
                phase === 'applied'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : totalFixes > 0
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-blue-50 border-blue-200 text-blue-900'
              }`} data-testid="data-health-summary">
                {report.summary}
                {phase === 'preview' && totalFixes === 0 && (
                  <div className="mt-1 font-semibold">No issues detected. Your data is clean.</div>
                )}
              </div>

              <SectionList
                title="Missing valid_until (will be back-filled to quote_date + 30 days)"
                testid="section-valid-until"
                items={report.valid_until_backfilled}
                renderItem={(i) => <span>{i.quote_number} — set to {i.new_valid_until?.slice(0,10)}</span>}
              />

              <SectionList
                title="Inverse-orphan receipts (linked to quote's ledger, but receipt.quote_id is null)"
                testid="section-inverse-orphans"
                items={report.inverse_orphans_repaired}
                renderItem={(i) => (
                  <span>{i.receipt_number} → will link to {i.linked_to_quote} (balance CI${(i.balance_remaining || 0).toFixed(2)})</span>
                )}
              />

              <div className="grid grid-cols-3 gap-3">
                <MetricPill label="Quotes normalised" value={report.datetime_normalised?.quotes || 0} testid="metric-quotes-normalised" />
                <MetricPill label="Invoices normalised" value={report.datetime_normalised?.invoices || 0} testid="metric-invoices-normalised" />
                <MetricPill label="Receipts normalised" value={report.datetime_normalised?.receipts || 0} testid="metric-receipts-normalised" />
              </div>

              {report.errors?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs">
                  <div className="font-semibold mb-1">{report.errors.length} record(s) errored during scan:</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {report.errors.slice(0, 5).map((e, idx) => (
                      <li key={idx}><span className="font-mono">{e.kind}/{e.id?.slice(0,8)}</span> — {e.error}</li>
                    ))}
                    {report.errors.length > 5 && <li>…and {report.errors.length - 5} more</li>}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-border bg-gray-50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-white transition-colors"
          >
            {phase === 'applied' ? 'Close' : 'Cancel'}
          </button>
          {phase === 'preview' && !loading && !error && (
            <button
              onClick={handleApply}
              disabled={applying || totalFixes === 0}
              data-testid="data-health-apply-btn"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <ShieldCheck className="w-4 h-4" />
              {applying ? 'Applying…' : `Apply ${totalFixes} fix${totalFixes === 1 ? '' : 'es'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MetricPill = ({ label, value, testid }) => (
  <div className="p-3 rounded-lg border border-border bg-white text-center" data-testid={testid}>
    <div className="text-xl font-bold text-foreground">{value}</div>
    <div className="text-xs text-foreground/60 mt-0.5">{label}</div>
  </div>
);

const SectionList = ({ title, testid, items, renderItem }) => {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold text-foreground/70 mb-1 uppercase tracking-wide">
        {title} ({items.length})
      </div>
      <div className="border border-border rounded-lg divide-y divide-border max-h-40 overflow-y-auto text-sm">
        {items.map((it, idx) => (
          <div key={idx} className="px-3 py-2">{renderItem(it)}</div>
        ))}
      </div>
    </div>
  );
};
